import React, { useEffect, useState } from "react";
import { Form, Radio, Select, Spin } from "antd";

import { ILookupOption, lookupsApi } from "../../../api/lookupsApi";
import { TemplateScope } from "../../../types/models/presentations";

/**
 * Who a template is for.
 *
 * Presented as one choice rather than two independent fields, because the
 * resolver treats it as one: bespoke beats destination beats default. A template
 * carrying both would match its named properties *and* everything in the
 * destination, while reporting itself as "This property only" — so the API
 * rejects the combination and this asks the question in the shape of the answer.
 */

export interface IScopeValue {
  scope: TemplateScope;
  destination: number | null;
  listings: number[];
}

export function scopeOf(
  destination: number | null | undefined,
  listings: number[] | undefined
): TemplateScope {
  if (listings && listings.length > 0) return "listing";
  if (destination) return "destination";
  return "default";
}

/** Search that ignores accents, the same as the drawer's — most names are Spanish. */
const fold = (value: string) =>
  value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

function TemplateScopeFields({ scope }: { scope: TemplateScope }) {
  const [destinations, setDestinations] = useState<ILookupOption[]>([]);
  const [listings, setListings] = useState<ILookupOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    Promise.all([lookupsApi.destinations(), lookupsApi.listings()])
      .then(([d, l]) => {
        if (!live) return;
        setDestinations(d);
        setListings(l);
      })
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, []);

  const options = (records: ILookupOption[]) =>
    records.map((record) => ({ value: record.id, label: record.name }));

  const search = (input: string, option?: { label?: string }) =>
    fold(option?.label ?? "").includes(fold(input));

  return (
    <>
      <Form.Item name="scope" label="Use this template for">
        <Radio.Group>
          <Radio.Button value="default">Every property</Radio.Button>
          <Radio.Button value="destination">One destination</Radio.Button>
          <Radio.Button value="listing">Specific properties</Radio.Button>
        </Radio.Group>
      </Form.Item>

      {loading && <Spin size="small" />}

      {scope === "destination" && (
        <Form.Item
          name="destination"
          label="Destination"
          rules={[{ required: true, message: "Pick a destination" }]}
          extra="Every property here uses this template, unless one of them has a template of its own."
        >
          <Select
            showSearch
            allowClear
            loading={loading}
            placeholder="Search destinations"
            optionFilterProp="label"
            filterOption={search}
            options={options(destinations)}
          />
        </Form.Item>
      )}

      {scope === "listing" && (
        <Form.Item
          name="listings"
          label="Properties"
          rules={[{ required: true, message: "Pick at least one property" }]}
          extra="These properties use this template instead of any destination or default one."
        >
          <Select
            mode="multiple"
            showSearch
            loading={loading}
            placeholder="Search properties"
            optionFilterProp="label"
            filterOption={search}
            options={options(listings)}
          />
        </Form.Item>
      )}

      {scope === "default" && (
        <p style={{ color: "rgba(0,0,0,.45)", marginTop: -8 }}>
          Used for any property with no more specific template of its own.
        </p>
      )}
    </>
  );
}

export default TemplateScopeFields;
